import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnyRow, BenchmarkLevel, BenchmarkPayload, Capabilities, ClassReportPayload, ClassRow, Grain, isVendorRow, PartError, ProductRowsPayload, ProductSearchField, ProductSearchResult, ReportKey, ReportParams, Status, Tier, VendorRow, VendorRowsParams, VendorRowsPayload } from '../../../interfaces/reports/WeeklyPlanning.interface';
import WeeklyPlanningApi, { WeeklyPlanningApiError } from '../../../services/reports/WeeklyPlanningReports.service';
import { buildRowTree, FlatRow, hasScopeFilter, HierarchyView, isProductView, MAX_PRODUCT_CLASS_IDS, needsVendorRows, passingClassRows, RowTreeFilters, RowTreeInput, RowTreeSearch, skuParentKeys, splitLoadKey, tooManyClassesText, totalsViewOf, applyPlanEdits, PlanEdits, PlanEditChanges, BenchmarkContext, CellContext, columnsFor, currentEpochMonth, currentEpochWeek, formatPeriodLabel, metricById, MetricDefinition, periodContaining, withTrendedOtb, AttentionItem, benchmarkLabel, benchmarkMonthParam, clientBenchmark, needsAttention, NeedsAttentionOptions, optionsFromRows, resolveRowBenchmarks, totalRows, applyTierFallback, columnStateFor, defaultViewState, DYNAMIC_COLUMN_IDS, FRESHNESS_IDS, queryParamsOf, readUrlState, useReportViewState, UseReportViewStateResult, withColumnVisibility, withGrain, capabilityAllows } from './logic';
import type { ReportColumnState } from './ui';
import AuthProvider from '../../../providers/AuthProvider';
export const BATCH_SIZE = 25;

export interface LazyChildRows {
  rows: Record<string, VendorRow[] | undefined>;
  payloads: Record<string, ProductRowsPayload | undefined>;
  noticesByParent: Record<string, string | undefined>;
  failed: Record<string, boolean | undefined>;
  load: (parentKey: string) => void;
  /** Load several parents, in as few requests as the options allow (see the header). */
  loadMany: (parentKeys: string[]) => void;
}

export interface UseLazyChildRowsOptions {
  resetKey: string;
  params: ReportParams;
  fetch: (parentKey: string, params: ReportParams) => Promise<ProductRowsPayload>;
  /** One request for several parents: parent -> payload; a parent missing from the answer counts as failed. Without it `loadMany` loads one by one. */
  fetchMany?: (parentKeys: string[], params: ReportParams) => Promise<Record<string, ProductRowsPayload | undefined>>;
  /** Which parents `fetchMany` accepts (default: all of them). */
  canBatch?: (parentKey: string) => boolean;
  failureNotice: (parentKey: string, error: unknown) => string;
  /** Whether a failed parent may be asked again on its next `load`; default true. */
  retryable?: (parentKey: string, error: unknown) => boolean;
}

interface Held { rows: LazyChildRows['rows']; payloads: LazyChildRows['payloads']; noticesByParent: LazyChildRows['noticesByParent']; failed: LazyChildRows['failed'] }
const EMPTY: Held = { rows: {}, payloads: {}, noticesByParent: {}, failed: {} };
const without = <T,>(rec: Record<string, T>, key: string): Record<string, T> => {
  if (!(key in rec)) return rec;
  const { [key]: _drop, ...rest } = rec;
  return rest;
};

export function useLazyChildRows({ resetKey, params, fetch, fetchMany, canBatch, failureNotice, retryable }: UseLazyChildRowsOptions): LazyChildRows {
  const [held, setHeld] = useState<Held>(EMPTY);
  const requested = useRef(new Set<string>());
  const loaded = useRef(new Set<string>());
  const retry = useRef(new Set<string>()); // failed, retryable: still "requested" (a reset re-asks), the next load asks again
  const gen = useRef(0);
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const fetchRef = useRef(fetch);
  fetchRef.current = fetch;
  const noticeRef = useRef(failureNotice);
  noticeRef.current = failureNotice;
  const retryRef = useRef(retryable);
  retryRef.current = retryable;
  const fetchManyRef = useRef(fetchMany);
  fetchManyRef.current = fetchMany;
  const canBatchRef = useRef(canBatch);
  canBatchRef.current = canBatch;

  // What a settled parent adds to the held state; the ref bookkeeping (loaded / retry) is done by the caller once per parent.
  const holdLoaded = (h: Held, parentKey: string, p: ProductRowsPayload): Held => ({
    rows: { ...h.rows, [parentKey]: p.rows }, payloads: { ...h.payloads, [parentKey]: p },
    noticesByParent: without(h.noticesByParent, parentKey), failed: without(h.failed, parentKey),
  });
  const holdFailed = (h: Held, parentKey: string, notice: string, again: boolean): Held => (again
    ? { ...h, noticesByParent: { ...h.noticesByParent, [parentKey]: notice }, failed: { ...h.failed, [parentKey]: true } }
    : { ...h, rows: { ...h.rows, [parentKey]: [] }, noticesByParent: { ...h.noticesByParent, [parentKey]: notice }, failed: without(h.failed, parentKey) });
  /** Records a failure in the refs and returns the state change. */
  const failure = (parentKey: string, e: unknown): ((h: Held) => Held) => {
    const notice = noticeRef.current(parentKey, e);
    const again = retryRef.current ? retryRef.current(parentKey, e) : true;
    if (again) retry.current.add(parentKey);
    else loaded.current.add(parentKey);
    return (h) => holdFailed(h, parentKey, notice, again);
  };

  const request = useCallback((parentKey: string) => {
    requested.current.add(parentKey);
    retry.current.delete(parentKey);
    const g = gen.current;
    let pending: Promise<ProductRowsPayload>;
    try {
      pending = fetchRef.current(parentKey, paramsRef.current);
    } catch (e) {
      pending = Promise.reject(e);
    }
    pending.then(
      (p) => {
        if (g !== gen.current) return;
        loaded.current.add(parentKey);
        setHeld((h) => holdLoaded(h, parentKey, p));
      },
      (e) => {
        if (g !== gen.current) return;
        setHeld(failure(parentKey, e));
      }
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- holdLoaded / failure read refs and setters only

  const requestMany = useCallback((keys: string[]) => {
    keys.forEach((k) => { requested.current.add(k); retry.current.delete(k); });
    const g = gen.current;
    let pending: Promise<Record<string, ProductRowsPayload | undefined>>;
    try {
      pending = (fetchManyRef.current as NonNullable<typeof fetchMany>)(keys, paramsRef.current);
    } catch (e) {
      pending = Promise.reject(e);
    }
    pending.then(
      (byKey) => {
        if (g !== gen.current) return;
        const steps = keys.map((k) => {
          const p = byKey?.[k];
          if (p) { loaded.current.add(k); return (h: Held) => holdLoaded(h, k, p); }
          return failure(k, new Error(`No answer for ${k}`));
        });
        setHeld((h) => steps.reduce((acc, step) => step(acc), h));
      },
      (e) => {
        if (g !== gen.current) return;
        const steps = keys.map((k) => failure(k, e));
        setHeld((h) => steps.reduce((acc, step) => step(acc), h));
      }
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- holdLoaded / failure read refs and setters only

  const load = useCallback((parentKey: string) => {
    if (!requested.current.has(parentKey) || retry.current.has(parentKey)) request(parentKey);
  }, [request]);

  const loadMany = useCallback((parentKeys: string[]) => {
    const todo = Array.from(new Set(parentKeys)).filter((k) => !requested.current.has(k) || retry.current.has(k));
    const batchable = fetchManyRef.current ? todo.filter((k) => (canBatchRef.current ? canBatchRef.current(k) : true)) : [];
    todo.filter((k) => !batchable.includes(k)).forEach(request);
    for (let i = 0; i < batchable.length; i += BATCH_SIZE) {
      const chunk = batchable.slice(i, i + BATCH_SIZE);
      if (chunk.length === 1) request(chunk[0]);
      else requestMany(chunk);
    }
  }, [request, requestMany]);

  const seenKey = useRef(resetKey);
  useEffect(() => {
    if (seenKey.current === resetKey) return;
    seenKey.current = resetKey;
    const stillLoading = Array.from(requested.current).filter((k) => !loaded.current.has(k));
    gen.current += 1;
    requested.current = new Set();
    loaded.current = new Set();
    retry.current = new Set();
    setHeld(EMPTY);
    loadMany(stillLoading);
  }, [resetKey, loadMany]);

  return useMemo(() => ({ ...held, load, loadMany }), [held, load, loadMany]);
}

// W3 (D15): the server-side product search behind the search box. `q` is the trimmed text, or null while the box is
// hidden or the text is shorter than MIN_SEARCH_LENGTH (nothing is asked then and the view is the unfiltered one). The box already
// debounces typing (SEARCH_DEBOUNCE_MS), so every change of `q` here is a request, except one already answered for the same client,
// text and window (kept in a small per-client cache, so an edit back to an earlier text is instant). While a new text is loading the
// previous result stays in `result` (the table does not flash back to the full view between keystrokes); `loading` says one is in flight.
// A failure keeps no result for that text: `error` carries it and the view falls back to the unfiltered one with a notice.
// Cleared / hidden (`q` null): result and error are dropped at once.

export const MIN_SEARCH_LENGTH = 2;
export const MAX_SEARCH_LENGTH = 100;
const CACHE_SIZE = 20;

export interface ProductSearchState {
  loading: boolean;
  /** The answer for `q` (or, while a new `q` loads, for the one before it). Null before the first answer and after the box clears. */
  result: ProductSearchResult | null;
  /** The text `result` answers. */
  resultQ: string | null;
  error: WeeklyPlanningApiError | null;
}

const IDLE: ProductSearchState = { loading: false, result: null, resultQ: null, error: null };

/** The search text as the server will read it: trimmed, inner whitespace folded; null when it is too short (or too long) to search. */
export const searchQueryOf = (text: string): string | null => {
  const q = text.replace(/\s+/g, ' ').trim();
  const chars = Array.from(q).length; // code points, as the server counts (mb_strlen); `q.length` counts UTF-16 units, so an emoji would count twice
  return chars >= MIN_SEARCH_LENGTH && chars <= MAX_SEARCH_LENGTH ? q : null;
};

/** The parameters that change the answer (the window that ranks matches); every other report parameter is dropped from the cache key. */
const windowKey = (params: ReportParams): string => `${params.grain}|${params.target}|${params.lookback}`;

export function useProductSearch(clientId: number | string, q: string | null, params: ReportParams): ProductSearchState {
  const [state, setState] = useState<ProductSearchState>(IDLE);
  const cache = useRef<{ client: string; entries: Map<string, ProductSearchResult> }>({ client: String(clientId), entries: new Map() });
  const wk = windowKey(params);
  const paramsRef = useRef(params);
  paramsRef.current = params;
  if (cache.current.client !== String(clientId)) cache.current = { client: String(clientId), entries: new Map() };

  useEffect(() => {
    if (q === null) {
      setState((s) => (s === IDLE ? s : IDLE));
      return undefined;
    }
    const key = `${q.toLowerCase()}|${wk}`;
    const hit = cache.current.entries.get(key);
    if (hit) {
      setState({ loading: false, result: hit, resultQ: q, error: null });
      return undefined;
    }
    let live = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    WeeklyPlanningApi.searchProducts(clientId, q, paramsRef.current).then(
      (result) => {
        if (!live) return;
        cache.current.entries.set(key, result);
        if (cache.current.entries.size > CACHE_SIZE) cache.current.entries.delete(cache.current.entries.keys().next().value as string);
        setState({ loading: false, result, resultQ: q, error: null });
      },
      (e) => {
        if (!live) return;
        const error = e instanceof WeeklyPlanningApiError ? e : new WeeklyPlanningApiError('failed', null, (e as Error)?.message || 'The search could not be completed.');
        setState({ loading: false, result: null, resultQ: null, error });
      }
    );
    return () => { live = false; };
  }, [clientId, q, wk]);

  return useMemo(() => state, [state]);
}

/** The fields the box names: the last answer's, else the capabilities', else all four (an older backend). */
export const searchFieldsOf = (result: ProductSearchResult | null, capabilityFields?: ProductSearchField[]): ProductSearchField[] =>
  result?.fields ?? capabilityFields ?? ['title', 'sku', 'barcode', 'tags'];

// a payload's `errors[]` names the part that failed by its internal key ("priorSales: Could not load priorSales.").
// Buyers get a sentence about what is missing instead; the raw key goes to the console (once per key) for whoever has to fix it.
// Every sentence says "couldn't" so the notice list keeps it visible as a load failure (NoticeList PRIORITY).

const INVENTORY = "Inventory couldn't load; stock, cover and the figures built on them may be missing.";
const ON_ORDER = "On order couldn't load; on-order, OTB and cover figures may be missing.";
const LAST_YEAR = "Last year's figures couldn't load; the LY columns and rows may be blank.";
const SALES = "Sales couldn't load; sales figures may be missing or incomplete.";

export const PART_ERROR_NOTICES: Readonly<Record<string, string>> = {
  priorSales: "Last year's sales for reorder suggestions couldn't load; suggestions may be missing.",
  freshness: "Aging couldn't load; Fresh % and the aging columns may be blank.",
  actuals: SALES, skuActuals: SALES, vendorActuals: SALES,
  currentInventory: INVENTORY, inventory: INVENTORY, inventoryAnchor: INVENTORY, skuInventory: INVENTORY, vendorInventory: INVENTORY,
  onOrder: ON_ORDER, skuOnOrder: ON_ORDER, vendorOnOrder: ON_ORDER, onOrderThroughPlan: ON_ORDER, vendorOnOrderThroughPlan: ON_ORDER,
  vendorOnOrderThisPeriod: "On order due this week or month couldn't load; the Hold / Take tags may be missing.",
  lastYear: LAST_YEAR, lastYearHistory: LAST_YEAR, lastYearPrefetch: LAST_YEAR, trendLastYear: LAST_YEAR,
  plans: "Plans couldn't load; plan, OTB and status figures may be missing.",
  trend: "Trended sales couldn't be worked out; trend columns may be blank.",
  salesThrough: "The latest sales date couldn't be read; trend columns may be blank.",
  dimension: "The class and location list couldn't load; the report may be incomplete.",
  classRows: "The class rows couldn't load.",
  capabilities: "What data this client has couldn't be checked; some columns may be hidden.",
  benchmarks: "Benchmarks couldn't load.",
};

const GENERIC = "Some data couldn't load; figures may be missing.";
const logged = new Set<string>();

/** The notice a buyer sees for one part error; the raw part key and message are logged once. */
export const partErrorNotice = (e: PartError): string => {
  const key = e.part ?? '';
  if (!logged.has(`${key}|${e.message}`)) {
    logged.add(`${key}|${e.message}`);
    // eslint-disable-next-line no-console -- the raw part key for support; the buyer sees the sentence
    console.warn(`[weekly-planning] part error "${key || 'unknown'}": ${e.message}`);
  }
  if (e.part && PART_ERROR_NOTICES[e.part]) return PART_ERROR_NOTICES[e.part];
  return e.part ? GENERIC : e.message;
};

/** Test hook: forget what was logged. */
export const resetPartErrorLog = (): void => logged.clear();

// a small in-memory LRU for the class report payloads, so switching back to a combination of
// request params that was loaded a moment ago shows at once, with no refetch. The key is the FULL request (client,
// report and every param the server sees: grain, target, lookback, compare, basis, span, multiplier, ...), so
// anything that changes the answer is a different entry, and another client's payload can never be served.
// Entries also expire (`ttlMs`): the cache is a speed-up for switching around, not a copy of the server's data.

export const PAYLOAD_CACHE_SIZE = 8;
export const PAYLOAD_CACHE_TTL_MS = 5 * 60 * 1000;

interface Entry<V> { value: V; at: number }

export class LruCache<V> {
  private readonly entries = new Map<string, Entry<V>>();

  constructor(private readonly max: number = PAYLOAD_CACHE_SIZE, private readonly ttlMs: number = PAYLOAD_CACHE_TTL_MS, private readonly now: () => number = Date.now) {}

  get size(): number { return this.entries.size; }

  /** The cached value (made the most recent), or undefined when absent or expired. */
  get(key: string): V | undefined {
    const e = this.entries.get(key);
    if (!e) return undefined;
    if (this.now() - e.at > this.ttlMs) {
      this.entries.delete(key);
      return undefined;
    }
    this.entries.delete(key);
    this.entries.set(key, e);
    return e.value;
  }

  has(key: string): boolean {
    const e = this.entries.get(key);
    return !!e && this.now() - e.at <= this.ttlMs;
  }

  set(key: string, value: V): void {
    this.entries.delete(key);
    this.entries.set(key, { value, at: this.now() });
    while (this.entries.size > this.max) {
      const oldest = this.entries.keys().next().value as string;
      this.entries.delete(oldest);
    }
  }

  delete(key: string): void { this.entries.delete(key); }

  clear(): void { this.entries.clear(); }
}

/** JSON with sorted keys, so the same params always make the same key. */
const stable = (v: unknown): string => {
  if (Array.isArray(v)) return `[${v.map(stable).join(',')}]`;
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    return `{${Object.keys(o).filter((k) => o[k] !== undefined).sort().map((k) => `${JSON.stringify(k)}:${stable(o[k])}`).join(',')}}`;
  }
  return JSON.stringify(v) ?? 'null';
};

export const payloadCacheKey = (clientId: string | number, report: string, params: object): string => `${clientId}|${report}|${stable(params)}`;

// W3 (D15): what the table needs from a /product-search answer. The endpoint returns matching products with the
// class and vendor parents they sit under; the table loads those parents' rows through /product-rows and /sku-rows (the same lazy
// loaders as an expand) and filters them to the matching products (rowTree's `productIds` search), parents expanded. Pure helpers.

/** The Show SKUs parents (rowTree's SKU_PARENT_RE): class:<id>, vendor:<key>, vendor:<key>:class:<id>. */
const SKU_PARENT_RE = /^(class:\d+|vendor:[^:]+(:class:\d+)?)$/;

interface Parents { classIds: Set<number>; vendorKeys: Set<string>; pairs: Set<string>; categoryKeys: Set<string> }

const parentsOf = (result: ProductSearchResult, classRows: ClassRow[]): Parents => {
  const classIds = new Set<number>();
  const vendorKeys = new Set<string>();
  const pairs = new Set<string>();
  result.products.forEach((p) => p.parents.forEach((q) => {
    classIds.add(q.locationClassId);
    vendorKeys.add(q.vendorKey);
    pairs.add(`${q.vendorKey}|${q.locationClassId}`);
  }));
  const categoryKeys = new Set<string>();
  classRows.forEach((r) => {
    if (typeof r.locationClassId === 'number' && classIds.has(r.locationClassId)) categoryKeys.add(`category:${typeof r.categoryId === 'number' ? r.categoryId : 'none'}`);
  });
  return { classIds, vendorKeys, pairs, categoryKeys };
};

/** Whether a product-rows parent (class:<id>, classes:<ids>, vendor:<key>[:classes:<ids>], category:<id|none>) holds a matching product. */
const productParentHolds = (key: string, p: Parents): boolean => {
  let m = /^class:(\d+)$/.exec(key);
  if (m) return p.classIds.has(Number(m[1]));
  m = /^classes:(\d+(?:,\d+)*)$/.exec(key);
  if (m) return m[1].split(',').some((id) => p.classIds.has(Number(id)));
  m = /^vendor:([^:]+)(?::classes:(\d+(?:,\d+)*))?$/.exec(key);
  if (m) return p.vendorKeys.has(m[1]) && (m[2] === undefined || m[2].split(',').some((id) => p.pairs.has(`${m![1]}|${id}`)));
  return p.categoryKeys.has(key);
};

/** Whether a SKU-rows parent (class:<id>, vendor:<key>, vendor:<key>:class:<id>) holds a matching product. */
const skuParentHolds = (key: string, p: Parents): boolean => {
  let m = /^class:(\d+)$/.exec(key);
  if (m) return p.classIds.has(Number(m[1]));
  m = /^vendor:([^:]+):class:(\d+)$/.exec(key);
  if (m) return p.pairs.has(`${m[1]}|${m[2]}`);
  m = /^vendor:([^:]+)$/.exec(key);
  return !!m && p.vendorKeys.has(m[1]);
};

/**
 * The parents whose rows must be loaded for the matches to show: `baseTree` is the view's tree, expanded and with no search (its
 * product parents carry `productParent`; under Show SKUs its leaves are the SKU parents), `result` the endpoint's answer. Product
 * views give product-rows parents, the others sku-rows parents; Total by Product has none (its one parent comes from
 * `totalByProductSearchParent`). `skuParents`: the unmerged tree's SKU parents (Show SKUs). Sorted, de-duplicated; empty when nothing matched.
 */
export const searchLoadParents = (view: HierarchyView, baseTree: readonly FlatRow[], result: ProductSearchResult | null, classRows: ClassRow[], skuParents: readonly string[] = []): string[] => {
  if (!result || result.products.length === 0 || view === 'totalByProduct') return [];
  const p = parentsOf(result, classRows);
  const out = new Set<string>();
  if (isProductView(view)) {
    baseTree.forEach((f) => {
      if (f.productParent) splitLoadKey(f.productParent).forEach((k) => { if (productParentHolds(k, p)) out.add(k); });
    });
  } else {
    baseTree.forEach((f) => {
      if (isVendorRow(f.row) && f.row.level === 'sku') return;
      if (f.kind === 'leaf' && SKU_PARENT_RE.test(f.row.key) && skuParentHolds(f.row.key, p)) out.add(f.row.key);
    });
    // A chain row (`chain:<code>`) is no SKU parent: the stores' own parents come from the unmerged tree (rowTree `skuParentKeys`).
    skuParents.forEach((k) => { if (SKU_PARENT_RE.test(k) && skuParentHolds(k, p)) out.add(k); });
  }
  return Array.from(out).sort();
};

/**
 * Total by Product while searching: every product of a top-500 `all` load would miss matches outside the top 500, so the matches' own
 * classes are loaded instead (`classes:<ids>`, or `vendor:<key>:classes:<ids>` under a single-vendor Vendor filter), kept inside the
 * Location / Category / Class filters. undefined = use the view's own parent (no result, or more classes than one request takes);
 * null = no product can match inside the filters (the table is empty: no request).
 */
export const totalByProductSearchParent = (result: ProductSearchResult | null, classRows: ClassRow[], filters: Pick<RowTreeFilters, 'locations' | 'categories' | 'classes' | 'vendors'>): string | null | undefined => {
  if (!result) return undefined;
  const single = filters.vendors.codes.length === 1 && !filters.vendors.exclude ? filters.vendors.codes[0] : null;
  const inScope = hasScopeFilter(filters) ? new Set(passingClassRows(classRows, filters).map((r) => r.locationClassId as number)) : null;
  const ids = new Set<number>();
  result.products.forEach((pr) => pr.parents.forEach((q) => {
    if ((single === null || q.vendorKey === single) && (inScope === null || inScope.has(q.locationClassId))) ids.add(q.locationClassId);
  }));
  if (ids.size === 0) return null;
  if (ids.size > MAX_PRODUCT_CLASS_IDS) return undefined;
  const list = Array.from(ids).sort((a, b) => a - b).join(',');
  return single !== null ? `vendor:${single}:classes:${list}` : `classes:${list}`;
};

/** The productIds a search keeps (rowTree `RowTreeSearch.productIds`). */
export const matchedProductIds = (result: ProductSearchResult | null): Set<string> | null => (result ? new Set(result.products.map((p) => p.productId)) : null);

// The shared data/state hook of the three weekly planning reports (Plan 4; Plan 3 hand-off §8).
// Decisions recorded here:
// - useReportViewState needs capabilities at mount. Until they arrive it gets PENDING_CAPS, which
// permit everything, so a `?v=` share link is not degraded by a guess; the real capabilities then
// re-run the tier fallback. Without `?v=` the view is reset to the real defaults once (no spurious
// "Weekly → Monthly" notices for a Monthly-only client) and Monthly is seeded from `epochMonth`.
// Nothing is fetched before that, so the first request already carries the right grain and target.
// "Was there a share link" uses the scoped decode (`readUrlState(report, clientId)`): a `?v=` the
// kit wrote for another client or report (remounts, tabs sharing `v`) counts as none.
// - A `clientId` change resets everything client-specific during render (view re-initialised to the
// new client's defaults and seed, expansion, payloads, SKU rows, notices); capabilities and payloads
// are tagged with / guarded by the client they were asked for, and every fetch effect's cleanup
// invalidates its in-flight request. The page shell also mounts with `key={String(clientId)}`
// as a second line of defence.
// - Two fetch effects: the class report keyed on the base params by value (queryParams without
// allocWindow / allocBasis), and vendor rows keyed on the vendor params, only while the view
// needs vendor rows (`needsVendorRows`: Vendor by Class, Total by Vendor, Class by Vendor, Vendor by
// Product) and cached until those params change. So the hierarchy view and the allocation settings
// never refetch the class report (spec 5.3). A vendor-rows failure falls back to the class tree
// with a notice; an `unauthorized` one is the session-expired error.
// - SKU rows belong to one base-param set: a base change drops them and re-issues every key that was
// still loading, under the new params, so a mounted "Loading SKUs…" placeholder is never stranded.
// - `flatRows` is what the table shows; `totalsRows` is the expandAll tree, the only input of
// `attention` (and of the pageModel KPI / chart helpers a page calls), built on `totalsViewOf(view)`
//: product views and Class by Vendor total their classes, Total by Vendor its vendors.
// - `expanded` is keyed by `FlatRow.expandKey` (product rows are scoped `<parent>/<key>`).
// - Product views: product rows load through useLazyChildRows keyed on client + base params
// (Total by Product eagerly, under `totalByProductParent`: `all`, `vendor:<key>` or the filtered
// `classes:<ids>`, keyed by that exact string and never merged; the others when a parent is expanded,
// via `loadChildRows`, which the table's placeholders call: product rows in a product view, SKU rows
// otherwise). Show SKUs never applies there (the Product / SKU grain does: variants arrive with their
// products). KPIs and attention use `totalsViewOf(view)`, so they never move as products load; vendor
// rows are fetched for every view `needsVendorRows`. A Total-by-Product parent of null sends nothing
// (empty table + the rule's notice); a `classes:` request answered 414 or failing before Laravel
// (status null) shows rule (d)'s "narrow the filter" notice and is final; other failures retry on the
// next expand. Under a Location / Category / Class filter, Category / Vendor by Product load filtered
// scopes (`classes:<ids>`, `vendor:<key>:classes:<ids>`, rowTree). Product failure and payload notices
// show only for the parents in the current tree (`activeProductParents`). Product rows never hold
// the page in 'loading': they load inside the table, so the KPIs stay on screen.
// - Host month: `synced` is the month host and hook agree on (the seed, or the `?v=` period at init). A
// changed `epochMonth` prop in Monthly moves the period there (no echo); a month the user PICKS in the
// report (`state.period` set) is emitted through `setEpochMonth`. The default month (no period: first
// load, reset, a grain switch, a saved view) is never pushed to the host (review: the default
// must not move the other reports on the page). Weekly ignores the prop and never emits.
// - Default period: a view with no period shows `defaultPeriod`, the period containing
// `capabilities.salesThrough` (the last sales day; never later than today's period), else the calendar
// period. It is derived, never written into the state, so a share link's or a picked period wins and
// a saved view (no period) falls back to it. `viewReady` says the capabilities are in and the view is
// initialised (reset / seeded): the page waits for it before applying a `?sv=` saved view.
// - Benchmarks: a third fetch effect keyed on client + benchmark month + level + retry nonce,
// run once the class payload is in (it gives the grain and target). Weekly asks for the latest complete
// month (null); Monthly for the target month. The benchmark month never touches the report's key, a
// benchmark failure or status is only a notice (the report and its KPIs stand), and `benchmarkLevel`
// 'none' (or capabilities without the field) sends no request. `ctx.benchmarks` is resolved once per
// payload + benchmark payload + scope filter, before the row tree, so benchmark columns sort.

// - Speed-ups: class payloads are kept in an in-memory LRU (about 8, expiring after a few minutes) keyed by the
// client, report and the full base request params, so switching back to a combination loaded a moment ago shows at
// once with no request and no loading state. The cache belongs to one client + report (a switch drops it, and the
// key carries the client anyway), a retry bypasses it, and a failed request is never cached. A request already in
// flight for a key is joined, not repeated. The OTHER grain (Weekly <-> Monthly) is fetched in the background only on
// hover or focus of the grain toggle (`prefetchOtherGrain`; never on load, each prefetch is a second BigQuery read),
// with the params the grain switch itself would send, so that switch is instant; a prefetch failure is silent, a
// payload that answers another grain is not cached, and the `prefetchOtherGrain: false` option turns it off.
/** how long a changed Total by Product scope (filter ticks) waits before its product rows are requested. */
export const PRODUCT_SCOPE_DEBOUNCE_MS = 300;
/** On in the app; off under Jest unless a test asks (page-level tests count requests). */
const PREFETCH_BY_DEFAULT = process.env.NODE_ENV !== 'test';

export interface UseWeeklyPlanningReportOptions {
  reportKey: ReportKey;
  clientId: string | number;
  /** The host's month: seeds the Monthly period (no `?v=`, not the current month) and is followed when it changes. */
  epochMonth: number | null;
  /** Called when the Monthly period changes in the report (a pick, or a reset to the current month). */
  setEpochMonth?: (m: number) => void;
  attentionOptions?: NeedsAttentionOptions;
  /** Allow the hover / focus prefetch of the other grain (default true; false under Jest). */
  prefetchOtherGrain?: boolean;
}

export type WeeklyPlanningReportStatus = 'loading' | 'ready' | 'error' | 'unsupported';

export interface WeeklyPlanningReportModel {
  reportKey: ReportKey; clientId: string | number; userId: string | number | null;
  /** Plan & OTB what-if edits: each edited cell's original plan sales (row key → period → figure); `payload` carries the edited plan. */
  planEditOriginals: PlanEdits;
  /** What-if edits: plan BOM raised because an inflow would have gone negative (row key → period → amount); the matrix marks those cells. */
  planEditRaised: PlanEdits;
  /** What-if edits (8a): every other cell an edit moved (row key → period | 'span' → field → the figure before); the matrix marks those cells. */
  planEditChanged: PlanEditChanges;
  capabilities: Capabilities | null; tier: Tier | null;
  view: UseReportViewStateResult;
  status: WeeklyPlanningReportStatus;
  error: WeeklyPlanningApiError | null; retry: () => void;
  payload: ClassReportPayload | null; vendorPayload: VendorRowsPayload | null;
  ctx: CellContext | null;
  columns: MetricDefinition[]; columnState: ReportColumnState; onColumnState: (s: ReportColumnState) => void;
  /** The rows the table shows (honours `expanded`). */
  flatRows: FlatRow[];
  /** `buildRowTree({...input, expandAll: true})`: feed this to kpiTotals / charts; `attention` uses it. */
  totalsRows: FlatRow[];
  expanded: Record<string, boolean>; toggleGroup: (key: string) => void;
  loadSkuRows: (parentKey: string) => void; skuRowsByParent: Record<string, VendorRow[] | undefined>;
  /** Product views: rows per product parent (the exact parent string sent); payloads keep the truncation fields. */
  productRowsByParent: Record<string, VendorRow[] | undefined>; productPayloads: Record<string, ProductRowsPayload | undefined>;
  /** What the table's placeholders call: product rows in a product view, SKU rows otherwise. */
  loadChildRows: (parentKey: string) => void;
  attention: AttentionItem[];
  filterOptions: ReturnType<typeof optionsFromRows>;
  /** View-state fallback notices + payload notices + part errors + vendor / SKU load failures, de-duplicated. */
  notices: string[];
  /** Today's period (the payload's `currentPeriod`): "This week" in the picker. */
  currentPeriod: number;
  /** The period shown when the view has none: the one containing capabilities.salesThrough, else `currentPeriod`. */
  defaultPeriod: number;
  /** The capabilities are in and the view is initialised (the first fetch may still be in flight). */
  viewReady: boolean;
  /** The target period is a Sales Trend period (`payload.trend.periods` holds it): the trend columns show by default. */
  trendPeriod: boolean;
  /** the benchmark payload for the current client + benchmark month (null while loading, failed, or level 'none'). */
  benchmarks: BenchmarkPayload | null;
  /** `capabilities.benchmarkLevel`; 'none' until the capabilities are in or when the backend sends none. */
  benchmarkLevel: BenchmarkLevel;
  /** fetch the other grain's default payload into the cache (the Weekly / Monthly toggle calls it on hover / focus). A no-op when off, cached, in flight or the client has one grain. */
  prefetchOtherGrain: () => void;
  /** W3: the server-side product search behind the search box. */
  productSearch: ProductSearchModel;
}

/**
 * The search box's state. `visible`: product or SKU rows are in the view (product hierarchies, or Show SKUs on) on a client with SKU-level
 * data. `applied`: the table is filtered to `result`'s products. `fields`: what the client's POS lets us match (the box names these).
 */
export interface ProductSearchModel {
  visible: boolean; applied: boolean; loading: boolean; tooShort: boolean;
  fields: ProductSearchField[];
  result: ProductSearchResult | null;
  error: string | null;
}

/** No search: the row tree's filter-only input. */
const NO_SEARCH: RowTreeSearch = { text: '', scope: 'product' };

const PENDING_CAPS: Capabilities = {
  clientId: 0, tier: 'A', hasDailyActuals: true, hasItemLevelDaily: true, hasV3Plans: true, weeklyMode: true, skuLevel: true, hasReceiving: true,
  skuDetailSources: null, actualsSource: '', planSource: '', degraded: false,
};
export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Sign in again.';

type ClientId = string | number;
interface ClassFetched { loading: boolean; payload: ClassReportPayload | null; error: WeeklyPlanningApiError | null }
interface VendorFetched { key: string | null; payload: VendorRowsPayload | null; error: WeeklyPlanningApiError | null }
interface Skus { rows: Record<string, VendorRow[] | undefined>; notices: string[] }
interface BenchFetched { key: string; payload: BenchmarkPayload | null; error: WeeklyPlanningApiError | null }
const CLASS_INITIAL: ClassFetched = { loading: true, payload: null, error: null };
const VENDOR_INITIAL: VendorFetched = { key: null, payload: null, error: null };
const SKUS_EMPTY: Skus = { rows: {}, notices: [] };

export function useWeeklyPlanningReport({ reportKey, clientId, epochMonth, setEpochMonth, attentionOptions, prefetchOtherGrain = PREFETCH_BY_DEFAULT }: UseWeeklyPlanningReportOptions): WeeklyPlanningReportModel {
  const auth = AuthProvider.useAuth() as { user?: { id?: string | number } | null } | null; // as VP does
  const userId: string | number | null = auth?.user?.id ?? null;
  const [caps, setCaps] = useState<{ client: ClientId; caps: Capabilities } | null>(null);
  const [capsError, setCapsError] = useState<{ client: ClientId; error: WeeklyPlanningApiError } | null>(null);
  const [capsNonce, setCapsNonce] = useState(0);
  const [fetchNonce, setFetchNonce] = useState(0);
  const [initialised, setInitialised] = useState(false);
  const [classFetched, setClassFetched] = useState<ClassFetched>(CLASS_INITIAL);
  const [vendorFetched, setVendorFetched] = useState<VendorFetched>(VENDOR_INITIAL);
  const [skus, setSkus] = useState<Skus>(SKUS_EMPTY);
  const [bench, setBench] = useState<BenchFetched | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [grainHint, setGrainHint] = useState<Grain>('weekly');
  const [seenClient, setSeenClient] = useState<ClientId>(clientId);
  if (seenClient !== clientId) {
    // Client switch: drop everything client-specific before anything commits (React re-renders at once).
    setSeenClient(clientId);
    setInitialised(false);
    setClassFetched(CLASS_INITIAL);
    setVendorFetched(VENDOR_INITIAL);
    setSkus(SKUS_EMPTY);
    setBench(null);
    setExpanded({});
  }
  const capabilities = caps && caps.client === clientId ? caps.caps : null;
  const urlHadView = useRef(readUrlState(reportKey, clientId) !== null); // scoped: another client's / report's ?v= is no share link
  const payload = classFetched.payload;

  useEffect(() => {
    let live = true;
    setCapsError(null);
    WeeklyPlanningApi.getCapabilities(clientId).then(
      (c) => { if (live) setCaps({ client: clientId, caps: c }); },
      (e) => { if (live) setCapsError({ client: clientId, error: toReportError(e) }); }
    );
    return () => { live = false; };
  }, [clientId, capsNonce]);

  const currentPeriod = payload && payload.grain === grainHint ? payload.currentPeriod : grainHint === 'weekly' ? currentEpochWeek() : currentEpochMonth();
  // open on the period containing the last sales day (on a Sunday with sales through Wednesday
  // the calendar week is empty); never a future period. No sales date (Tier C, or no sales in 90 days):
  // the calendar period. Nothing is fetched before the capabilities arrive, so the first request has it.
  const salesThrough = capabilities?.salesThrough ?? null;
  const defaultPeriod = salesThrough ? Math.min(periodContaining(salesThrough, grainHint), currentPeriod) : currentPeriod;
  const view = useReportViewState({ reportKey, clientId, capabilities: capabilities ?? PENDING_CAPS, currentPeriod: defaultPeriod });
  const { state, queryParams, set } = view;
  if (state.grain !== grainHint) setGrainHint(state.grain); // re-renders before commit, so effects see the right period

  const skuRequested = useRef(new Set<string>());
  const skuLoaded = useRef(new Set<string>());
  const skuGen = useRef(0);
  const synced = useRef<number | null>(null);
  const prevProp = useRef<number | null | undefined>(undefined);
  const firstClient = useRef(true);
  useEffect(() => {
    if (firstClient.current) { firstClient.current = false; return; }
    urlHadView.current = false; // a share link was for the previous client
    skuGen.current += 1;
    skuRequested.current = new Set();
    skuLoaded.current = new Set();
  }, [clientId]);

  // Once the real capabilities are in: real defaults (unless `?v=` was given), the Monthly seed, then fetch.
  useEffect(() => {
    if (!capabilities || initialised) return;
    if (urlHadView.current) {
      synced.current = state.grain === 'monthly' ? state.period ?? defaultPeriod : null;
    } else {
      const fresh = applyTierFallback(defaultViewState(reportKey, capabilities.weeklyMode ? 'weekly' : 'monthly'), capabilities, reportKey).state;
      if (JSON.stringify(fresh) !== JSON.stringify(state)) view.reset();
      const monthly = fresh.grain === 'monthly';
      const seed = monthly && typeof epochMonth === 'number' && epochMonth >= 0 && epochMonth !== currentEpochMonth() ? epochMonth : null;
      if (seed !== null) set({ period: seed });
      synced.current = monthly ? seed : null;
    }
    prevProp.current = epochMonth;
    setInitialised(true);
  }, [capabilities, initialised]); // eslint-disable-line react-hooks/exhaustive-deps -- runs once, when the capabilities arrive; state / set / epochMonth are read at that moment, not subscribed to
  const viewReady = initialised && capabilities !== null;

  // benchmarks load after the class payload (they need its grain and target), in their own effect and
  // cache key, so they never refetch or fail the report. Weekly: the latest complete month (null); Monthly: the target.
  const benchmarkLevel: BenchmarkLevel = capabilities?.benchmarkLevel ?? 'none';
  const benchMonth = payload ? benchmarkMonthParam(payload.grain, payload.targetPeriod) : null;
  const benchKey = viewReady && payload && benchmarkLevel !== 'none' ? `${clientId}|${benchMonth ?? 'latest'}|${benchmarkLevel}|${fetchNonce}` : null;
  const benchFetchedKey = useRef<string | null>(null);
  benchFetchedKey.current = bench?.key ?? null;
  useEffect(() => {
    if (!benchKey || benchFetchedKey.current === benchKey) return undefined;
    let live = true;
    WeeklyPlanningApi.getBenchmarks(clientId, benchMonth).then(
      (p) => { if (live) setBench({ key: benchKey, payload: p, error: null }); },
      (e) => { if (live) setBench({ key: benchKey, payload: null, error: toReportError(e) }); },
    );
    return () => { live = false; };
  }, [benchKey]); // eslint-disable-line react-hooks/exhaustive-deps -- benchKey already encodes client, month, level and retry nonce; clientId / benchMonth are read from the same render
  const benchCurrent = benchKey !== null && bench !== null && bench.key === benchKey;
  const benchmarks = benchCurrent ? bench.payload : null;
  const benchError = benchCurrent ? bench.error : null;
  const benchmarkCtx = useMemo<BenchmarkContext | null>(() => {
    // No client entity (not_entitled, unavailable, no_vertical, no_client_rows, month_out_of_window): nothing to show; the status notice says why.
    if (!payload || !benchmarks || benchmarks.level === 'none' || !benchmarks.client) return null;
    const filters = state.filters;
    return {
      byRowKey: resolveRowBenchmarks(payload.rows, benchmarks, filters),
      client: clientBenchmark(payload.rows, benchmarks, filters),
      label: benchmarkLabel(benchmarks),
      monthLabel: benchmarks.monthLabel,
      isFallback: benchmarks.isFallback,
      level: benchmarks.level,
      weeksInMonth: benchmarks.weeksInMonth,
    };
  }, [payload, benchmarks, state.filters]);

  // Labels for SKU-failure notices are looked up lazily from the current payloads.
  const payloadRef = useRef(payload);
  payloadRef.current = payload;
  const vendorPayloadRef = useRef(vendorFetched.payload);
  vendorPayloadRef.current = vendorFetched.payload;
  /** Marks SKU parents as loaded with no rows and adds each one's "Could not load SKUs" notice. */
  const failSku = (parentKeys: string[]) => {
    const rows: AnyRow[] = [...(payloadRef.current?.rows ?? []), ...(vendorPayloadRef.current?.rows ?? [])];
    setSkus((s) => ({
      rows: { ...s.rows, ...Object.fromEntries(parentKeys.map((k) => [k, [] as VendorRow[]])) },
      notices: [...s.notices, ...parentKeys.map((k) => `Could not load SKUs for ${labelOf(rows.find((r) => r.key === k), k)}.`)],
    }));
  };

  // One request for several SKU parents (a chain's member stores): each parent is held as if requested alone.
  const requestSkuBatch = useCallback((parentKeys: string[], params: ReportParams) => {
    parentKeys.forEach((k) => skuRequested.current.add(k));
    const gen = skuGen.current;
    WeeklyPlanningApi.getSkuRowsBatch(clientId, parentKeys, params).then(
      (byParent) => {
        if (gen !== skuGen.current) return;
        const lacking = parentKeys.filter((k) => !byParent[k]);
        setSkus((s) => {
          const rows = { ...s.rows };
          parentKeys.forEach((k) => { if (byParent[k]) rows[k] = byParent[k].rows; });
          return { ...s, rows };
        });
        parentKeys.forEach((k) => skuLoaded.current.add(k));
        if (lacking.length) failSku(lacking);
      },
      () => {
        if (gen !== skuGen.current) return;
        parentKeys.forEach((k) => skuLoaded.current.add(k));
        failSku(parentKeys);
      }
    );
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps -- failSku reads refs and the setter only (same as requestSku below)

  const requestSku = useCallback((parentKey: string, params: ReportParams) => {
    skuRequested.current.add(parentKey);
    const gen = skuGen.current;
    WeeklyPlanningApi.getSkuRows(clientId, parentKey, params).then(
      (p) => {
        if (gen !== skuGen.current) return;
        skuLoaded.current.add(parentKey);
        setSkus((s) => ({ ...s, rows: { ...s.rows, [parentKey]: p.rows } }));
      },
      () => {
        if (gen !== skuGen.current) return;
        skuLoaded.current.add(parentKey);
        const rows: AnyRow[] = [...(payloadRef.current?.rows ?? []), ...(vendorPayloadRef.current?.rows ?? [])];
        const label = labelOf(rows.find((r) => r.key === parentKey), parentKey);
        setSkus((s) => ({ rows: { ...s.rows, [parentKey]: [] }, notices: [...s.notices, `Could not load SKUs for ${label}.`] }));
      }
    );
  }, [clientId]);

  const baseKey = JSON.stringify(baseParamsOf(queryParams));
  const baseParams = useMemo<ReportParams>(() => JSON.parse(baseKey), [baseKey]);
  const baseParamsRef = useRef(baseParams);
  baseParamsRef.current = baseParams;
  const requestId = useRef(0);
  // One cache and one in-flight map per client + report (a switch makes new ones: nothing of the old client is served or written).
  const cache = useMemo(() => new LruCache<ClassReportPayload>(PAYLOAD_CACHE_SIZE, PAYLOAD_CACHE_TTL_MS), [clientId, reportKey]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally new per client + report only (a switch must drop the old client's cache)
  const inflight = useMemo(() => new Map<string, Promise<ClassReportPayload>>(), [clientId, reportKey]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally new per client + report only (a switch must drop the old client's cache)
  const loadClassReport = useCallback((key: string, params: ReportParams): Promise<ClassReportPayload> => {
    const joined = inflight.get(key);
    if (joined) return joined;
    // Only the request that still owns the key may free it or write the cache: a Retry replaces the entry, and the
    // superseded request finishing late must neither delete the new one's entry nor cache its own (stale) payload.
    const request: Promise<ClassReportPayload> = WeeklyPlanningApi.getReport(reportKey, clientId, params).then(
      (p) => {
        if (inflight.get(key) === request) {
          inflight.delete(key);
          if (p.grain === params.grain) cache.set(key, p); // a payload that answers another grain is shown (as before) but never reused
        }
        return p;
      },
      (e) => { if (inflight.get(key) === request) inflight.delete(key); throw e; },
    );
    inflight.set(key, request);
    return request;
  }, [cache, inflight, reportKey, clientId]);
  const handledNonce = useRef(fetchNonce);
  useEffect(() => {
    if (!viewReady) return undefined;
    const id = ++requestId.current;
    const stillLoading = Array.from(skuRequested.current).filter((k) => !skuLoaded.current.has(k));
    skuGen.current += 1;
    skuRequested.current = new Set();
    skuLoaded.current = new Set();
    setSkus(SKUS_EMPTY);
    stillLoading.forEach((k) => requestSku(k, baseParams));
    const key = payloadCacheKey(clientId, reportKey, baseParams);
    if (handledNonce.current !== fetchNonce) { // Retry: ask the server again
      handledNonce.current = fetchNonce;
      cache.delete(key);
      inflight.delete(key);
    }
    const cached = cache.get(key);
    if (cached) {
      setClassFetched({ loading: false, payload: cached, error: null });
      return () => { requestId.current += 1; };
    }
    setClassFetched((f) => ({ ...f, loading: true, error: null }));
    loadClassReport(key, baseParams).then(
      (p) => { if (id === requestId.current) setClassFetched({ loading: false, payload: p, error: null }); },
      (e) => { if (id === requestId.current) setClassFetched({ loading: false, payload: null, error: toReportError(e) }); }
    );
    return () => { requestId.current += 1; };
  }, [clientId, reportKey, baseParams, viewReady, fetchNonce]); // eslint-disable-line react-hooks/exhaustive-deps -- the helpers (cache, inflight, requestSku, loadClassReport) are stable per client + report; refetch only on these inputs

  // Prefetch the other grain on intent only: the Weekly / Monthly toggle calls `prefetchOtherGrain` on hover or
  // focus, so the click that follows finds the payload in the cache. Never on load: most visits never switch grain, and each
  // prefetch is a second BigQuery read. The params are the ones the grain toggle would send (same tier fallback, period
  // cleared, that grain's default period). Repeats are free (cached or already in flight); a failure is silent.
  const loadedGrain = classFetched.payload && !classFetched.loading ? classFetched.payload.grain : null;
  const prefetchOtherGrainNow = useCallback(() => {
    if (!prefetchOtherGrain || !viewReady || !capabilities || loadedGrain === null || loadedGrain !== state.grain) return;
    const other: Grain = state.grain === 'weekly' ? 'monthly' : 'weekly';
    const next = applyTierFallback(withGrain(state, other), capabilities, reportKey).state;
    if (next.grain !== other) return; // the client cannot show that grain
    const current = other === 'weekly' ? currentEpochWeek() : currentEpochMonth();
    const target = capabilities.salesThrough ? Math.min(periodContaining(capabilities.salesThrough, other), current) : current;
    const params = baseParamsOf(queryParamsOf(next, target));
    const key = payloadCacheKey(clientId, reportKey, params);
    if (cache.has(key) || inflight.has(key)) return;
    loadClassReport(key, params).catch(() => undefined);
  }, [prefetchOtherGrain, viewReady, loadedGrain, state, clientId, reportKey, capabilities, cache, inflight, loadClassReport]);

  const inVendor = needsVendorRows(state.view);
  const vendorKey = viewReady && inVendor ? `${clientId}|${JSON.stringify(queryParams)}|${fetchNonce}` : null;
  const vendorFetchedKey = useRef<string | null>(null);
  vendorFetchedKey.current = vendorFetched.key;
  const vendorRequestId = useRef(0);
  useEffect(() => {
    if (!vendorKey || vendorFetchedKey.current === vendorKey) return undefined;
    const id = ++vendorRequestId.current;
    WeeklyPlanningApi.getVendorRows(clientId, queryParams as VendorRowsParams).then(
      (p) => { if (id === vendorRequestId.current) setVendorFetched({ key: vendorKey, payload: p, error: null }); },
      (e) => { if (id === vendorRequestId.current) setVendorFetched({ key: vendorKey, payload: null, error: toReportError(e) }); }
    );
    return () => { vendorRequestId.current += 1; };
  }, [vendorKey]); // eslint-disable-line react-hooks/exhaustive-deps -- vendorKey encodes the client, the query params and the retry nonce
  const vendorCurrent = vendorKey !== null && vendorFetched.key === vendorKey;
  const vendorError = vendorCurrent ? vendorFetched.error : null;
  const vendorPayload = inVendor ? vendorFetched.payload : null;
  const treeView: HierarchyView = inVendor && vendorError ? 'totalByClass' : state.view;

  // Product rows: one loader per client + base params; parents are canonical strings.
  const productView = isProductView(state.view) && capabilityAllows(capabilities, 'vendor');
  // Total by Product's "Top by inventory" ranks (and caps) the products by stock; it is a product-rows
  // param only (never the class payload's), and sent only in that view so every other product load keeps its request and cache key.
  const productSort: 'inventory' | null = state.view === 'totalByProduct' && state.productSort === 'inventory' ? 'inventory' : null;
  const productParams = useMemo<ReportParams>(() => (productSort ? { ...baseParams, sort: productSort } : baseParams), [baseParams, productSort]);
  const products = useLazyChildRows({
    resetKey: `${clientId}|${baseKey}${productSort ? `|${productSort}` : ''}`,
    params: productParams,
    fetch: (parent, params) => WeeklyPlanningApi.getProductRows(clientId, parent, params),
    // One request per chain expand: a chain row's member stores are `class:<id>` parents.
    fetchMany: (parents, params) => WeeklyPlanningApi.getProductRowsBatch(clientId, parents, params),
    canBatch: isClassParent,
    failureNotice: (parent, e) => {
      const ids = classIdCount(parent);
      if (ids > 0 && isTooLong(e)) return tooManyClassesNotice(ids);
      return `Could not load products for ${productParentLabel(parent, [...(payloadRef.current?.rows ?? []), ...(vendorPayloadRef.current?.rows ?? [])])}.`;
    },
    // A too-long class list fails the same way every time (rule (d)); anything else may be retried on the next expand.
    retryable: (parent, e) => !(classIdCount(parent) > 0 && isTooLong(e)),
  });
  const totalByProduct = productView && state.view === 'totalByProduct';
  // Total by Product: 'all' (top 500), one vendor, or the filtered scope; null = no request (notice says why).
  // every Location / Category / Class tick changes the scope, and each scope is its own product-rows request. In Total by
  // Product the filter -> parent path settles for PRODUCT_SCOPE_DEBOUNCE_MS first (rapid ticks send one request, for the last
  // scope); the first scope, other views, a cleared payload and a scope that sends no request apply at once.
  const scopeNow = useMemo(() => (payload ? totalByProductParent(payload.rows, state.filters) : null), [payload, state.filters]);
  const [settledScope, setSettledScope] = useState<TotalByProductScope | null>(scopeNow);
  useEffect(() => {
    if (scopeKey(scopeNow) === scopeKey(settledScope)) return undefined;
    if (!totalByProduct || scopeNow === null || scopeNow.parent === null || settledScope === null) {
      setSettledScope(scopeNow); // nothing to settle: the first scope, another view, no payload, or a scope that sends no request (a notice)
      return undefined;
    }
    const timer = setTimeout(() => setSettledScope(scopeNow), PRODUCT_SCOPE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [scopeNow, totalByProduct]); // eslint-disable-line react-hooks/exhaustive-deps -- settledScope is the value being debounced toward scopeNow; listing it would restart the timer on its own write
  const productScope = totalByProduct && settledScope !== null && scopeNow !== null && scopeNow.parent !== null ? settledScope : scopeNow;
  // W3: the product search (server side). The box exists only while product or SKU rows are in the view and the client has
  // SKU-level data. `searchApplied`: a result is in hand for a searchable text (the previous text's while a new one loads, so the table does
  // not flash back to the full view between keystrokes). Total by Product then loads the matches' own classes instead of its top-500 `all`.
  const skuSearchable = (state.showSkus || isProductView(state.view)) && capabilityAllows(capabilities, 'vendor');
  const searchQuery = skuSearchable ? searchQueryOf(state.search.text) : null;
  const search = useProductSearch(clientId, searchQuery, baseParams);
  const searchApplied = searchQuery !== null && search.result !== null;
  const searchResult = searchApplied ? search.result : null;
  const searchTotalParent = totalByProduct && searchApplied && payload ? totalByProductSearchParent(searchResult, payload.rows, state.filters) : undefined;
  const productParent = searchTotalParent !== undefined ? searchTotalParent : productScope ? productScope.parent : undefined;
  const loadProducts = products.load;
  useEffect(() => {
    if (viewReady && totalByProduct && typeof productParent === 'string') loadProducts(productParent);
  }, [viewReady, totalByProduct, productParent, baseKey, productSort, clientId, loadProducts]);

  // Host month: follow a changed prop (Monthly only), emit a period changed in the report.
  useEffect(() => {
    if (!viewReady || prevProp.current === epochMonth) return;
    prevProp.current = epochMonth;
    if (state.grain !== 'monthly' || typeof epochMonth !== 'number' || epochMonth < 0 || epochMonth === synced.current) return;
    synced.current = epochMonth;
    set({ period: epochMonth });
  }, [epochMonth, viewReady]); // eslint-disable-line react-hooks/exhaustive-deps -- reacts to the host's epochMonth prop only; state.grain and set are read when it changes
  const setEpochMonthRef = useRef(setEpochMonth);
  setEpochMonthRef.current = setEpochMonth;
  useEffect(() => {
    if (!viewReady || state.grain !== 'monthly' || state.period === null) return; // the default month is shown, not pushed
    if (state.period === synced.current) return;
    synced.current = state.period;
    setEpochMonthRef.current?.(state.period);
  }, [viewReady, state.grain, state.period]);

  const tier = capabilities?.tier ?? null;
  const ctx = useMemo<CellContext | null>(() => (payload ? {
    grain: payload.grain, targetPeriod: payload.targetPeriod, currentPeriod: payload.currentPeriod, lookback: payload.lookbackPeriods,
    compare: state.compare, basis: state.basis, multiplier: state.multiplier, wosOverride: state.wosOverride,
    allocation: { basis: state.allocBasis, window: state.allocWindow, grain: payload.grain },
    trendMethod: state.trendMethod, // Part B: view state only, never a fetch key (every method is in the payload)
    otbOnOrder: state.otbOnOrder, // the OTB texts say when every open order is counted
    salesThrough: payload.salesThrough ?? null,
    benchmarks: benchmarkCtx, // resolved per payload + benchmark payload + scope filter, never a fetch key
  } : null), [payload, state.compare, state.basis, state.multiplier, state.wosOverride, state.allocBasis, state.allocWindow, state.trendMethod, state.otbOnOrder, benchmarkCtx]);

  // the benchmark group is in the catalogue only when the client is entitled (benchmarkLevel !== 'none').
  const columns = useMemo(() => columnsFor(reportKey, tier, benchmarkLevel).map((c) => metricById(c.id)), [reportKey, tier, benchmarkLevel]);
  // The last known value holds while no payload is in (a client switch, a load): the trend columns do not
  // flicker off and back on between payloads.
  const [lastTrendPeriod, setLastTrendPeriod] = useState(false);
  const payloadTrendPeriod = payload ? !!payload.trend && Array.isArray(payload.trend.periods) && payload.trend.periods.includes(payload.targetPeriod) : null;
  if (payloadTrendPeriod !== null && payloadTrendPeriod !== lastTrendPeriod) setLastTrendPeriod(payloadTrendPeriod);
  const trendPeriod = payloadTrendPeriod ?? lastTrendPeriod;
  const columnState = useMemo(() => columnStateFor(state, reportKey, tier, { trendPeriod, benchmarkLevel }), [state, reportKey, tier, trendPeriod, benchmarkLevel]);
  const onColumnState = useCallback((next: ReportColumnState) => {
    const visible = { ...state.columns.visible, ...next.visible };
    // The table hands back every column's visibility: a trend column the user did not change and that is
    // still dynamic stays unset, so touching another column never pins it; one the user changed is stored.
    DYNAMIC_COLUMN_IDS.forEach((id) => { if (state.columns.visible[id] === undefined && next.visible[id] === columnState.visible[id]) delete visible[id]; });
    const merged = withColumnVisibility(
      { ...state, columns: { ...state.columns, order: next.order, widths: next.widths, pinned: next.pinned } },
      visible
    );
    // the Freshness switch follows the freshness columns picked in the Columns menu.
    const fresh = FRESHNESS_IDS.filter((id) => id in next.visible);
    set({ columns: merged.columns, showPctOfTotal: merged.showPctOfTotal, ...(fresh.length ? { freshnessShown: fresh.some((id) => next.visible[id]) } : {}) });
  }, [state, set, columnState]);

  // Hidden switches never shape rows: "Weekly-planned only" applies only where its switch shows
  // (Weekly with V3 plans), and the product search only while product or SKU rows can be shown (above).
  const weeklyPlannedApplied = state.weeklyPlannedOnly && state.grain === 'weekly' && !!capabilities?.hasV3Plans;
  // Plan & OTB's what-if plan sales edits (planEdits.ts) apply to the class rows before the tree is built, so every
  // subtotal, total, KPI and the CSV follow; `payload` stays the fetched one for the loaders and caches.
  const planEdited = useMemo(
    () => (payload && reportKey === 'plan_otb' ? applyPlanEdits(payload, state.planEdits[payload.grain] ?? {}) : null),
    [payload, reportKey, state.planEdits]
  );
  const shownPayload = planEdited?.payload ?? payload;
  const treeSearch = useMemo<RowTreeSearch>(
    () => (searchApplied && searchQuery !== null ? { text: searchQuery, scope: 'product', productIds: matchedProductIds(searchResult) ?? undefined } : NO_SEARCH),
    [searchApplied, searchQuery, searchResult]
  );
  const treeBase = useMemo<Omit<RowTreeInput, 'expanded'> | null>(() => (payload && ctx ? {
    distributeOtb: state.distributeOtb && reportKey !== 'plan_otb',
    showChildClasses: state.showChildClasses,
    classRows: withTrendedOtb((shownPayload ?? payload).rows, ctx), vendorRows: needsVendorRows(treeView) ? vendorPayload?.rows ?? [] : undefined, view: treeView,
    filters: { ...state.filters, statuses: state.filters.statuses as Status[] }, search: treeSearch, sort: state.sort,
    showSkus: state.showSkus && !isProductView(treeView), weeklyPlannedOnly: weeklyPlannedApplied, skuRowsByParent: skus.rows, ctx,
    productRowsByParent: products.rows, productParent: productParent ?? null, productGrain: state.productGrain, productFailed: products.failed,
  } : null), [payload, shownPayload, vendorPayload, ctx, treeView, state.filters, treeSearch, state.sort, state.showSkus, weeklyPlannedApplied, skus.rows, products.rows, productParent, state.productGrain, products.failed, state.distributeOtb, state.showChildClasses, reportKey]);
  // A search expands every parent so the matching products show under their classes / vendors.
  const flatRows = useMemo(() => (treeBase ? buildRowTree({ ...treeBase, expanded, ...(searchApplied ? { expandAll: true } : {}) }) : []), [treeBase, expanded, searchApplied]);
  // KPIs, charts, counts and attention are taken on the totals view (`totalsViewOf`): classes in the
  // product views and Class by Vendor, vendors in Total by Vendor, so they compare rows of one level
  // and never move as product or SKU rows load.
  // The product search filters the table only: the totals are the report's own (class / vendor rows), never narrowed to the matches.
  const totalsRows = useMemo(() => (treeBase ? buildRowTree({ ...treeBase, search: NO_SEARCH, view: totalsViewOf(treeBase.view), expanded: {}, expandAll: true }) : []), [treeBase]);
  const toggleGroup = useCallback((key: string) => setExpanded((e) => ({ ...e, [key]: !e[key] })), []);

  const { salesBelowThreshold, agingThreshold, limit } = attentionOptions ?? {};
  const attention = useMemo(
    () => (ctx ? needsAttention(totalRows(totalsRows), ctx, { salesBelowThreshold, agingThreshold, limit }) : []),
    [totalsRows, ctx, salesBelowThreshold, agingThreshold, limit]
  );
  const filterOptions = useMemo(() => optionsFromRows(payload?.rows ?? [], vendorPayload?.rows), [payload, vendorPayload]);

  const loadSkuRows = useCallback((parentKey: string) => {
    if (!skuRequested.current.has(parentKey)) requestSku(parentKey, baseParamsRef.current);
  }, [requestSku]);
  // Branches on the tree actually shown (a vendor-rows failure falls back to the class tree, whose placeholders are SKU ones).
  // a chain row's placeholder loads every member store's rows (`chain-load:<key>|<key>`), merged by the row tree.
  // One request per chain expand: the member stores' parents go out together (`class:<id>` / `vendor:<key>:class:<id>`
  // parents in batches of at most BATCH_SIZE, everything else one by one), and each is held exactly as if it had been requested alone.
  const loadSkuRowsMany = useCallback((parentKeys: string[]) => {
    const todo = Array.from(new Set(parentKeys)).filter((k) => !skuRequested.current.has(k));
    const batchable = todo.filter((k) => skuBatchGroup(k) !== null);
    todo.filter((k) => !batchable.includes(k)).forEach((k) => requestSku(k, baseParamsRef.current));
    const groups = new Map<string, string[]>();
    batchable.forEach((k) => { const g = skuBatchGroup(k) as string; groups.set(g, [...(groups.get(g) ?? []), k]); });
    groups.forEach((keys) => {
      for (let i = 0; i < keys.length; i += BATCH_SIZE) {
        const chunk = keys.slice(i, i + BATCH_SIZE);
        if (chunk.length === 1) requestSku(chunk[0], baseParamsRef.current);
        else requestSkuBatch(chunk, baseParamsRef.current);
      }
    });
  }, [requestSku, requestSkuBatch]);
  const loadProductsMany = products.loadMany;
  const loadChildRows = useCallback((parentKey: string) => {
    const keys = splitLoadKey(parentKey);
    if (isProductView(treeView)) loadProductsMany(keys);
    else loadSkuRowsMany(keys);
  }, [treeView, loadProductsMany, loadSkuRowsMany]);

  // W3: load the parents the matches sit under (the same lazy loaders as an expand): product-rows parents in a product view,
  // sku-rows parents under Show SKUs. Total by Product loads its own search parent (above).
  const searchBaseTree = useMemo(() => (searchApplied && treeBase ? buildRowTree({ ...treeBase, search: NO_SEARCH, expanded: {}, expandAll: true }) : []), [searchApplied, treeBase]);
  const searchSkuParents = useMemo(() => (searchApplied && treeBase && treeBase.showSkus ? skuParentKeys({ ...treeBase, expanded: {} }) : []), [searchApplied, treeBase]);
  const searchParents = useMemo(() => (searchApplied && payload ? searchLoadParents(treeView, searchBaseTree, searchResult, payload.rows, searchSkuParents) : []), [searchApplied, payload, treeView, searchBaseTree, searchResult, searchSkuParents]);
  const searchParentsKey = searchParents.join('\n');
  useEffect(() => {
    if (searchParents.length === 0) return;
    if (isProductView(treeView)) loadProductsMany(searchParents);
    else loadSkuRowsMany(searchParents);
  }, [searchParentsKey, treeView, loadProductsMany, loadSkuRowsMany]); // eslint-disable-line react-hooks/exhaustive-deps -- searchParentsKey is searchParents by value

  // Product parents in the current tree (every product parent row of the expandAll tree, plus Total by
  // Product's parent): only their failure and payload notices show, so a "narrow it" / top-500 notice
  // goes away once the filter replaces its parent.
  const productTree = useMemo(() => (treeBase && isProductView(treeBase.view) ? buildRowTree({ ...treeBase, expanded: {}, expandAll: true }) : []), [treeBase]);
  const activeProductParents = useMemo(() => {
    const keys = new Set<string>();
    productTree.forEach((f) => { if (f.productParent) splitLoadKey(f.productParent).forEach((k) => keys.add(k)); });
    if (treeView === 'totalByProduct' && typeof productParent === 'string') keys.add(productParent);
    return Array.from(keys).sort().join('\n');
  }, [productTree, treeView, productParent]);

  const vendorNotice = vendorError && vendorError.kind !== 'unauthorized' ? `Vendor rows could not be loaded: ${vendorError.message}` : null;
  const scopeNotice = totalByProduct && productScope && productScope.parent === null ? productScope.notice : null;
  const notices = useMemo(() => Array.from(new Set([
    ...view.notices, ...payloadNotices(payload), ...payloadNotices(vendorPayload), ...(vendorNotice ? [vendorNotice] : []), ...skus.notices,
    ...(scopeNotice ? [scopeNotice] : []),
    ...activeProductParents.split('\n').filter(Boolean).flatMap((k) => [...(products.noticesByParent[k] ? [products.noticesByParent[k] as string] : []), ...payloadNotices(products.payloads[k] ?? null)]),
    ...(benchError ? ['Benchmarks could not be loaded.'] : []), // a benchmark failure is a notice, never the report's error
    ...(benchmarks ? [...benchmarks.notices, ...benchmarks.errors.map(partError), ...benchmarkStatusNotice(benchmarks)] : []),
  ])), [view.notices, payload, vendorPayload, vendorNotice, skus.notices, scopeNotice, activeProductParents, products.noticesByParent, products.payloads, benchError, benchmarks]);

  const capsErr = capsError && capsError.client === clientId ? capsError.error : null;
  const error = capsErr ?? (viewReady ? classFetched.error ?? (vendorError?.kind === 'unauthorized' ? vendorError : null) : null);
  const status: WeeklyPlanningReportStatus = error
    ? (error.kind === 'unsupported' ? 'unsupported' : 'error')
    : !viewReady || classFetched.loading || (inVendor && !vendorCurrent) ? 'loading' : 'ready'; // product rows load inside the table
  const retry = useCallback(() => (capsErr ? setCapsNonce((n) => n + 1) : setFetchNonce((n) => n + 1)), [capsErr]);

  const productSearch = useMemo<ProductSearchModel>(() => ({
    visible: skuSearchable, applied: searchApplied, loading: skuSearchable && search.loading,
    tooShort: skuSearchable && state.search.text.trim() !== '' && searchQuery === null,
    fields: searchFieldsOf(search.result, capabilities?.searchFields), result: searchResult,
    error: skuSearchable && searchQuery !== null && search.error ? search.error.message : null,
  }), [skuSearchable, searchApplied, search, state.search.text, searchQuery, capabilities, searchResult]);

  return {
    reportKey, clientId, userId, capabilities, tier, view, status, error, retry, payload: shownPayload, vendorPayload, ctx,
    planEditOriginals: planEdited?.originals ?? NO_PLAN_EDITS,
    planEditRaised: planEdited?.raised ?? NO_PLAN_EDITS,
    planEditChanged: planEdited?.changed ?? NO_PLAN_EDIT_CHANGES,
    columns, columnState, onColumnState, trendPeriod, flatRows, totalsRows, expanded, toggleGroup, loadSkuRows, skuRowsByParent: skus.rows,
    productRowsByParent: products.rows, productPayloads: products.payloads, loadChildRows,
    attention, filterOptions, notices, currentPeriod, defaultPeriod, viewReady, benchmarks, benchmarkLevel, prefetchOtherGrain: prefetchOtherGrainNow,
    productSearch,
  };
}

// ---- helpers ------------------------------------------------------------------------------------

const NO_PLAN_EDITS: PlanEdits = {};
const NO_PLAN_EDIT_CHANGES: PlanEditChanges = {};

/** Maps any thrown value to a WeeklyPlanningApiError; `unauthorized` gets the session-expired message. */
export const toReportError = (e: unknown): WeeklyPlanningApiError => {
  if (e instanceof WeeklyPlanningApiError) return e.kind === 'unauthorized' ? new WeeklyPlanningApiError('unauthorized', e.status, SESSION_EXPIRED_MESSAGE, e.payload) : e;
  return new WeeklyPlanningApiError('failed', null, (e as Error)?.message || 'The report could not be loaded.');
};

const partError = partErrorNotice;
const baseParamsOf = (p: ReportParams | VendorRowsParams): ReportParams => {
  const { allocWindow, allocBasis, ...rest } = p as VendorRowsParams;
  return rest;
};
const payloadNotices = (p: ClassReportPayload | VendorRowsPayload | ProductRowsPayload | null): string[] => (p ? [...(p.notices ?? []), ...(p.errors ?? []).map(partError)] : []);
/** the one notice a benchmark status earns (none for ok / not_entitled / unavailable: nothing to say, or the status line says it). */
const benchmarkStatusNotice = (b: BenchmarkPayload): string[] => {
  if (b.status === 'month_out_of_window') return [`No benchmark for this month: benchmarks cover the 24 complete months to ${b.latestMonth !== null ? formatPeriodLabel(b.latestMonth, 'monthly') : 'the latest month'}.`];
  if (b.status === 'no_vertical') return ['No benchmark peers: this client has no vertical in the benchmark data.'];
  if (b.status === 'no_client_rows') return ['No benchmark data for this client in that month.'];
  return [];
};

const labelOf = (row: AnyRow | undefined, key: string): string => {
  if (!row) return key;
  const cls = row.className || row.classCode;
  if (isVendorRow(row)) return cls && row.level !== 'vendor' ? `${row.vendor} · ${cls}` : row.vendor;
  return cls || key;
};

const tooManyClassesNotice = (n: number): string => tooManyClassesText(n, 'This filter', 'narrow it, or use Class / Category by Product.');
/** Number of class ids in a `classes:<ids>` / `vendor:<key>:classes:<ids>` parent; 0 for other parents. */
const classIdCount = (parent: string): number => {
  const m = /(?:^|:)classes:(\d+(?:,\d+)*)$/.exec(parent);
  return m ? m[1].split(',').length : 0;
};
/** A 414, or a request that failed before reaching Laravel (no HTTP status): the URL was too long to send. */
const isTooLong = (e: unknown): boolean => {
  const err = e as { kind?: string; status?: number | null } | null;
  return !!err && err.kind === 'failed' && (err.status === 414 || err.status === null || err.status === undefined);
};

const scopeKey = (sc: TotalByProductScope | null): string => (sc === null ? '' : sc.parent ?? `none:${sc.notice}`);
export type TotalByProductScope = { parent: string; notice?: undefined } | { parent: null; notice: string };

/**
 * Total by Product's parent from the class payload and the Location / Category / Class / Vendor filters
 *: (a) no Location / Category / Class filter → `all`, or
 * `vendor:<key>` when exactly one vendor is included; (b) no class passes → null, "No products in this
 * selection."; (c) 1..1,000 classes → `classes:<sorted, de-duplicated locationClassIds>`; (d) more → null,
 * the "narrow it" notice. A class passes when it, or an ancestor class, passes all three filters (a passing
 * parent keeps its subtree, as the class tree does); Unallocated and Unclassified rows are not classes. A
 * null parent sends no request; never fall back to `all`, never batch.
 */
export const totalByProductParent = (classRows: ClassRow[], filters: Pick<RowTreeFilters, 'locations' | 'categories' | 'classes' | 'vendors'>): TotalByProductScope => {
  if (!hasScopeFilter(filters)) {
    const v = filters.vendors;
    return v.codes.length === 1 && !v.exclude ? { parent: `vendor:${v.codes[0]}` } : { parent: 'all' };
  }
  const ids = Array.from(new Set(passingClassRows(classRows, filters).map((r) => r.locationClassId as number))).sort((a, b) => a - b);
  if (ids.length === 0) return { parent: null, notice: 'No products in this selection.' };
  if (ids.length > MAX_PRODUCT_CLASS_IDS) return { parent: null, notice: tooManyClassesNotice(ids.length) };
  return { parent: `classes:${ids.join(',')}` };
};

const productParentLabel = (key: string, rows: AnyRow[]): string => {
  if (key === 'all') return 'all products';
  if (key.startsWith('classes:')) return 'the selected classes';
  const v = /^(vendor:.+):classes:[\d,]+$/.exec(key);
  if (v) return labelOf(rows.find((r) => r.key === v[1]), v[1]);
  const m = /^category:(\d+|none)$/.exec(key);
  if (m) return rows.find((r) => String(r.categoryId ?? 'none') === m[1])?.category || '(No category)';
  return labelOf(rows.find((r) => r.key === key), key);
};


/** `class:<id>`: one store's class parent, the form a chain expand batches (product rows and SKU rows). */
const isClassParent = (parent: string): boolean => /^class:\d+$/.test(parent);

/**
 * The batch a SKU parent belongs to: `class:<id>` parents batch together ('class'), `vendor:<key>:class:<id>` parents with the same vendor
 * key batch together (the key); anything else is not batchable (null).
 */
const skuBatchGroup = (parent: string): string | null => {
  if (isClassParent(parent)) return 'class';
  const m = /^(vendor:[^:]+):class:\d+$/.exec(parent);
  return m ? m[1] : null;
};
